import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PaperPlaneTilt, FloppyDisk, Trash, Eye, EyeSlash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ admin_email: "", email_from_name: "", resend_api_key: "", currency_symbol: "" });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const { data: settings, isLoading, isError } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
    retry: false,
  });

  useEffect(() => {
    if (settings) {
      setForm({ admin_email: settings.admin_email || "", email_from_name: settings.email_from_name || "", resend_api_key: "", currency_symbol: settings.currency_symbol || "" });
    }
  }, [settings]);

  const save = async (keyOverride) => {
    setSaving(true);
    try {
      const payload = { ...form, resend_api_key: keyOverride !== undefined ? keyOverride : form.resend_api_key };
      const { data } = await api.put("/settings", payload);
      toast.success("Settings saved");
      setForm((f) => ({ ...f, resend_api_key: "" }));
      queryClient.setQueryData(["settings"], data);
      queryClient.invalidateQueries({ queryKey: ["public-config"] });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const removeKey = async () => {
    if (!window.confirm("Remove the Resend API key? Emails will fall back to the managed email service.")) return;
    await save("REMOVE");
  };

  const testEmail = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/test-email", {});
      toast.success(data.message);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTesting(false);
    }
  };

  if (isLoading) return <div className="py-16 text-center text-sm text-slate-400" data-testid="settings-loading">Loading settings…</div>;

  if (isError) {
    return (
      <div className="py-16 text-center" data-testid="settings-forbidden">
        <p className="text-sm font-semibold text-slate-700">You don't have permission to view settings.</p>
        <p className="mt-1 text-xs text-slate-400">Ask an admin to grant the settings.manage permission.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter text-slate-900">Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Configure how BakeFlow sends emails</p>
      </div>

      {/* Currency */}
      <div className="rounded-md border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-800">Currency</h2>
        </div>
        <div className="space-y-5 p-5">
          <div className="space-y-1.5">
            <Label htmlFor="currency-symbol">Currency symbol</Label>
            <div className="flex items-center gap-3">
              <Input
                id="currency-symbol"
                data-testid="settings-currency-symbol-input"
                value={form.currency_symbol}
                onChange={(e) => setForm({ ...form, currency_symbol: e.target.value })}
                placeholder="₹"
                className="w-28"
                maxLength={4}
              />
              <span className="text-sm text-slate-500">
                Preview: <span className="font-semibold text-slate-700">{form.currency_symbol || "₹"}100.00</span>
              </span>
            </div>
            <p className="text-xs text-slate-400">
              Symbol shown next to prices across the app (e.g. ₹, $, €, £). Leave blank to keep the default (₹).
            </p>
          </div>
          <div className="border-t border-slate-100 pt-4">
            <Button onClick={() => save()} disabled={saving} data-testid="settings-currency-save-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
              <FloppyDisk size={16} className="mr-1.5" /> {saving ? "Saving…" : "Save settings"}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="font-heading text-lg font-bold tracking-tight text-slate-800">Email Delivery</h2>
          <span
            data-testid="email-mode-badge"
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              settings?.mode === "resend" ? "bg-[#ECFDF5] text-[#047857]" : "bg-[#FFF7ED] text-[#C2410C]"
            }`}
          >
            {settings?.mode === "resend" ? "Own Resend account" : "Managed email (default)"}
          </span>
        </div>

        <div className="space-y-5 p-5">
          <div className="space-y-1.5">
            <Label>Admin email</Label>
            <Input
              data-testid="settings-admin-email-input"
              type="email"
              value={form.admin_email}
              onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
              placeholder="admin@company.com"
            />
            <p className="text-xs text-slate-400">
              Used as the sender address (with your own Resend key) and as the fallback recipient for digests and notifications.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Sender name</Label>
            <Input
              data-testid="settings-from-name-input"
              value={form.email_from_name}
              onChange={(e) => setForm({ ...form, email_from_name: e.target.value })}
              placeholder="BakeFlow"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Resend API key</Label>
            <div className="relative">
              <Input
                data-testid="settings-resend-key-input"
                type={showKey ? "text" : "password"}
                value={form.resend_api_key}
                onChange={(e) => setForm({ ...form, resend_api_key: e.target.value })}
                placeholder={settings?.has_resend_key ? `Configured: ${settings.resend_key_hint} (leave blank to keep)` : "re_xxxxxxxx (optional — leave blank to use managed email)"}
              />
              <button
                type="button"
                onClick={() => setShowKey(!showKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="settings-toggle-key-visibility"
              >
                {showKey ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <p className="text-xs text-slate-400">
              Paste your own Resend key to send via your Resend account (sender domain must be verified in Resend). Leave blank to keep the current key.
            </p>
            {settings?.has_resend_key && (
              <button onClick={removeKey} data-testid="settings-remove-key-button" className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700">
                <Trash size={12} /> Remove Resend key (use managed email)
              </button>
            )}
          </div>

          {settings?.updated_at && (
            <p className="text-xs text-slate-400" data-testid="settings-updated-at">
              Last updated by {settings.updated_by} · {new Date(settings.updated_at).toLocaleString()}
            </p>
          )}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            <Button onClick={() => save()} disabled={saving} data-testid="settings-save-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
              <FloppyDisk size={16} className="mr-1.5" /> {saving ? "Saving…" : "Save settings"}
            </Button>
            <Button onClick={testEmail} disabled={testing} variant="outline" data-testid="settings-test-email-button" className="border-[#EA580C] text-[#EA580C] hover:bg-[#FFF7ED]">
              <PaperPlaneTilt size={16} className="mr-1.5" /> {testing ? "Sending…" : "Send test email"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
